import ajax from "../helpers/ajax";
import StoreContainer from "../stores/StoreContainer";

import {
  EngagementRequest,
  EngagementContent,
  EngagementSettings,
  ChatLine,
  SubmitSurveyRequest,
  VisitorText,
  EngagementUpdate,
  ExitVisitorUpdate,
  RatingUpdate,
  LineRatingUpdate,
  PreEngagementRequest,
  VisitorBotMessage,
  VisitorBotMessageResult,
  BotFollowupModel,
  RouteToBotFollowupResponse,
  WebhookResult,
  CobrowsingRequest
} from "../types";

class EngagementService {
  static async loadContent(
    siteId: number,
    engagementRequest: EngagementRequest
  ): Promise<EngagementContent> {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementApiEndpoint()}/v1/${siteId}/engagement/requestengagement/`,
      method: "post",
      data: engagementRequest
    });
  }

  static async loadSettings(
    siteId: number,
    groupId: number
  ): Promise<EngagementSettings> {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementApiEndpoint()}/v1/${siteId}/settings`,
      data: { groupId }
    });
  }

  static async loadMessages(
    siteId: number,
    visitorId: string,
    engagementId: string
  ): Promise<Array<ChatLine>> {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementApiEndpoint()}/v1/${siteId}/engagement/getnewvisitorlines`,
      data: {
        visitorId,
        engagementId,
        maxLineId: 0
      }
    });
  }

  static async loadPreEngagementId(
    siteId: number,
    preEngagementRequest: PreEngagementRequest
  ): Promise<string> {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementApiEndpoint()}/v1/${siteId}/engagement/requestengagementid`,
      method: "post",
      data: preEngagementRequest
    });
  }

  static async emailVisitorTranscript(
    siteId: number,
    engagementId: string,
    emailAddress: string
  ): Promise<boolean> {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementApiEndpoint()}/v1/${siteId}/engagement/EmailVisitorTranscriptOnChatEnd`,
      method: "post",
      data: {
        engagementId,
        emailAddress
      }
    });
  }

  static async sendVisitorText(data: VisitorText) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${
        data.SiteId
      }/visitorchat/SendText`,
      method: "post",
      data: data
    });
  }

  static async sendInlineMinimized(siteId: number, data: EngagementUpdate) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/visitorchat/InlineMinimized`,
      method: "post",
      data: data
    });
  }

  static async sendFormToBot(
    siteId: number,
    data: any
  ): Promise<VisitorBotMessageResult> {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/visitorchat/SubmitBotForm`,
      method: "post",
      data: data
    });
  }

  static async sendMessageToBot(
    siteId: number,
    data: VisitorBotMessage
  ): Promise<VisitorBotMessageResult> {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/visitorchat/SendMessageToBot`,
      method: "post",
      data: data
    });
  }

  static async sendBotFollowupRouteToBot(
    siteId: number,
    data: BotFollowupModel
  ): Promise<RouteToBotFollowupResponse> {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/BotFollowup/RouteToBot`,
      method: "post",
      data: data
    });
  }

  static async sendBotFollowupRouteToGroup(
    siteId: number,
    data: BotFollowupModel
  ) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/BotFollowup/RouteToGroup`,
      method: "post",
      data: data
    });
  }

  static async sendBotFollowupRouteToAgent(
    siteId: number,
    data: BotFollowupModel
  ) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/BotFollowup/RouteToAgent`,
      method: "post",
      data: data
    });
  }

  static async sendBotFollowupClose(siteId: number, data: BotFollowupModel) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/BotFollowup/Close`,
      method: "post",
      data: data
    });
  }

  static async sendBotFollowupWebhook(
    siteId: number,
    data: BotFollowupModel
  ): Promise<WebhookResult> {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/BotFollowup/Webhook`,
      method: "post",
      data: data
    });
  }

  static async sendVisitorTypeahead(
    siteId: number,
    engagementId: string,
    text: string
  ) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/visitorchat/ReceiveVisitorTypeahead`,
      method: "post",
      data: { engagementId, text }
    });
  }

  static async setVisitorTyping(siteId: number, data: EngagementUpdate) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/visitorchat/SetTyping`,
      method: "post",
      data: data
    });
  }

  static async exitVisitor(siteId: number, data: ExitVisitorUpdate) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/visitorchat/ExitVisitor`,
      method: "post",
      data: data
    });
  }

  static async setStarRating(siteId: number, data: RatingUpdate) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/visitorchat/SetRating`,
      method: "post",
      data: data
    });
  }

  static async setLineRating(siteId: number, data: LineRatingUpdate) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/visitorchat/SetContentRating`,
      method: "post",
      data: data
    });
  }

  static async sendCobrowsingRequest(siteId: number, data: CobrowsingRequest) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementHubEndpoint()}/v1/${siteId}/visitorchat/SendCobrowsingRequestToAgent`,
      method: "post",
      data: data
    });
  }

  static async submitSurvey(
    siteId: number,
    data: SubmitSurveyRequest
  ): Promise<string> {
    return await ajax({
      url: `${StoreContainer.SettingStore.getEngagementApiEndpoint()}/v1/${siteId}/engagement/SubmitSurvey`,
      method: "post",
      data
    });
  }

  static async getKBArticles(siteId: number, searchQuery: Array<string>) {
    return await ajax({
      url: `${StoreContainer.SettingStore.getMainApiEndpoint()}/v1/${siteId}/KBSearch/Search`,
      method: "post",
      data: { searchQuery }
    });
  }
}

export default EngagementService;
